import { Link, useLocation } from "@remix-run/react";
import { ChevronDown, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLogoutUser } from "~/api/mutations";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { useMenuCategories } from "~/api/queries/useMenuCategories";
import isotipoImg from "~/assets/academia-isotipo.png";
import { Icon } from "~/components/Icon";
import { LogoutConfirmDialog } from "~/components/LogoutConfirmDialog";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { UserAvatar } from "~/components/UserProfile/UserAvatar";
import { USER_ROLE } from "~/config/userRoles";
import { cn } from "~/lib/utils";
import { CartIcon } from "~/modules/Cart/CartIcon";

type NavItem =
  | { href: string; key: string; children?: never }
  | { key: string; href: string; children: { href: string; key: string }[] };

// Navbar público — landing de lanzamiento (2026-07-24): single-page nav. "Cursos" y
// "Quiénes somos" SCROLLEAN a las secciones del Home (anclas /#), no navegan a páginas.
// Keys propias (coursesScroll/quienesSomos) para NO disparar el dropdown de categorías
// que se activa con key === "courses". Rutas/componentes ocultos siguen INTACTOS.
const navLinks: NavItem[] = [
  // 2026-07-31: "Cursos" y "Quiénes somos" OCULTOS. La home es la única superficie de
  // conversión del lanzamiento y el nav le competía al CTA. Las rutas y las páginas
  // siguen vivas (/courses, /quienes-somos) — sólo se sacaron del header.
  // { href: "/courses", key: "coursesScroll" },
  // { href: "/quienes-somos", key: "quienesSomos" },
  // { href: "/courses", key: "courses" },
  // {
  //   key: "servicios",
  //   href: "/servicios",
  //   children: [
  //     { href: "/servicios/capacitaciones", key: "capacitaciones" },
  //     { href: "/servicios/masterclasses", key: "masterclasses" },
  //     { href: "/servicios/talleres", key: "talleres" },
  //     { href: "/servicios/charlas", key: "charlas" },
  //     { href: "/servicios/consultoria", key: "consultoria" },
  //     { href: "/servicios/implementaciones", key: "implementaciones" },
  //   ],
  // },
  // { href: "/tools", key: "tools" },
  // { href: "/news", key: "news" },
  // { href: "/resources", key: "resources" },
  // { href: "/about", key: "about" },
];

function getRoleLabel(role: string | undefined, t: (key: string) => string): string {
  switch (role) {
    case USER_ROLE.admin:
      return t("landing.userMenu.roles.admin");
    case USER_ROLE.contentCreator:
      return t("landing.userMenu.roles.content_creator");
    case USER_ROLE.student:
    default:
      return t("landing.userMenu.roles.student");
  }
}

function UserDropdown() {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const { mutate: logout, isPending: isLoggingOut } = useLogoutUser();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  if (!currentUser) return null;

  // El email es nullable (los usuarios de WhatsApp no tienen), y UserAvatar espera un string.
  const userName =
    currentUser.firstName || currentUser.lastName
      ? `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim()
      : (currentUser.email ?? "");

  const isAdmin = currentUser.role === USER_ROLE.admin;
  const isContentCreator = currentUser.role === USER_ROLE.contentCreator;
  const isAdminLike = isAdmin || isContentCreator;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors select-none hover:bg-landing-surface focus:outline-none"
          >
            <UserAvatar
              userName={userName}
              profilePictureUrl={currentUser.profilePictureUrl}
              className="h-8 w-8"
            />
            <div className="hidden flex-col items-start sm:flex">
              <span className="text-sm font-medium text-neutral-900 leading-tight">{userName}</span>
              <span className="text-xs text-neutral-500 leading-tight">
                {getRoleLabel(currentUser.role, t)}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 text-neutral-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild>
            <Link
              to="/library"
              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
            >
              <Icon name="Course" className="h-4 w-4" />
              {t("landing.userMenu.platform")}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to={`/profile/${currentUser.id}`}
              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
            >
              <Icon name="User" className="h-4 w-4" />
              {t("landing.userMenu.profile")}
            </Link>
          </DropdownMenuItem>

          {isAdminLike && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  to="/admin/analytics"
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
                >
                  <Icon name="ChartNoAxes" className="h-4 w-4" />
                  {t("landing.userMenu.analytics")}
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/users"
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
                    >
                      <Icon name="Hat" className="h-4 w-4" />
                      {t("landing.userMenu.users")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/categories"
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
                    >
                      <Icon name="Category" className="h-4 w-4" />
                      {t("landing.userMenu.categories")}
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem asChild>
                <Link
                  to="/admin/courses"
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-landing-surface"
                >
                  <Icon name="Course" className="h-4 w-4" />
                  {t("landing.userMenu.myCourses")}
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              setLogoutConfirmOpen(true);
            }}
            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-error-600 hover:bg-error-50 hover:text-error-700"
          >
            <LogOut className="h-4 w-4" />
            {t("landing.userMenu.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        pending={isLoggingOut}
        onConfirm={() => logout()}
      />
    </>
  );
}

function MobileUserMenu() {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const { mutate: logout, isPending: isLoggingOut } = useLogoutUser();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  if (!currentUser) return null;

  // El email es nullable (los usuarios de WhatsApp no tienen), y UserAvatar espera un string.
  const userName =
    currentUser.firstName || currentUser.lastName
      ? `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim()
      : (currentUser.email ?? "");

  const isAdmin = currentUser.role === USER_ROLE.admin;
  const isContentCreator = currentUser.role === USER_ROLE.contentCreator;
  const isAdminLike = isAdmin || isContentCreator;
  const isStudent = currentUser.role === USER_ROLE.student;

  return (
    <>
      <div className="border-t border-landing-border pt-4 mt-4">
        <div className="flex items-center gap-3 px-3 py-2">
          <UserAvatar
            userName={userName}
            profilePictureUrl={currentUser.profilePictureUrl}
            className="h-10 w-10"
          />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-landing-text">{userName}</span>
            <span className="text-xs text-neutral-500">{getRoleLabel(currentUser.role, t)}</span>
          </div>
        </div>
        <div className="mt-2 space-y-1">
          <Link
            to={isStudent ? "/mi-aula" : "/library"}
            className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
          >
            {t("landing.userMenu.myCourse")}
          </Link>
          <Link
            to="/library"
            className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
          >
            {t("landing.userMenu.platform")}
          </Link>
          <Link
            to={`/profile/${currentUser.id}`}
            className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
          >
            {t("landing.userMenu.profile")}
          </Link>

          {isAdminLike && (
            <>
              <div className="my-2 border-t border-landing-border" />
              <Link
                to="/admin/analytics"
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
              >
                {t("landing.userMenu.analytics")}
              </Link>
              {isAdmin && (
                <>
                  <Link
                    to="/admin/users"
                    className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
                  >
                    {t("landing.userMenu.users")}
                  </Link>
                  <Link
                    to="/admin/categories"
                    className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
                  >
                    {t("landing.userMenu.categories")}
                  </Link>
                </>
              )}
              <Link
                to="/admin/courses"
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-landing-muted hover:bg-landing-surface"
              >
                {t("landing.userMenu.myCourses")}
              </Link>
            </>
          )}

          <div className="my-2 border-t border-landing-border" />
          <button
            type="button"
            onClick={() => setLogoutConfirmOpen(true)}
            className="block w-full rounded-lg px-3 py-2 text-left text-base font-medium text-error-600 hover:bg-error-50"
          >
            {t("landing.userMenu.logout")}
          </button>
        </div>
      </div>
      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        pending={isLoggingOut}
        onConfirm={() => logout()}
      />
    </>
  );
}

function getCategoryTitle(title: string | Record<string, string> | object): string {
  if (typeof title === "string") return title;
  const record = title as Record<string, string>;
  return record?.es || Object.values(record || {})[0] || "";
}

export function LandingHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [coursesExpanded, setCoursesExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // OJO con estos tres estados. `!!currentUser` los colapsaba en dos: mientras la
  // consulta de sesión viaja, `currentUser` es undefined — exactamente igual que cuando
  // NO hay sesión — así que el header afirmaba "deslogueado" cuando todavía no sabía, y
  // al llegar la respuesta saltaba a mostrar al usuario. Ese era el parpadeo al refrescar
  // con sesión abierta. Además el HTML del SSR nunca conoce la sesión, así que el estado
  // "no sé" es el que sale servido y hay que renderizarlo como tal.
  const { data: currentUser, isPending: authPending } = useCurrentUser();
  const isLoggedIn = !!currentUser;
  const isStudent = currentUser?.role === USER_ROLE.student;
  // Destino y etiqueta del botón principal del usuario. Los admin/creadores siguen yendo
  // al catálogo; el alumno va a su aula.
  const classroomHref = isStudent ? "/mi-aula" : "/library";
  const { data: menuCategories } = useMenuCategories();

  // El logo necesita siempre una superficie clara propia. El header transparente mezclaba el rojo
  // del isotipo con las auroras del fondo y destruía contraste justo en el primer viewport.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b bg-[#FBF8F0] transition-[box-shadow,border-color] duration-300 dark:bg-[#1D1916]",
        scrolled
          ? "border-landing-border shadow-[0_12px_32px_rgba(31,26,21,0.08)]"
          : // `border-black/[0.07]` no existe: `black` es var(--color-black) opaca, sin
            // <alpha-value>, así que Tailwind no emite la clase. `border-b` quedaba sin color
            // válido y caía al #e5e7eb del preflight: una línea casi blanca bajo un header
            // #1D1916 en oscuro, en todas las páginas públicas, que "se arreglaba sola" al
            // scrollear porque ahí sí entra un token válido. Se usa el mismo token del estado
            // scrolleado, más suave.
            "border-landing-border/60",
      )}
    >
      <nav className="font-landing mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-cuelume-hover="tick"
          data-cuelume-press
          className="font-display flex items-center gap-2 text-2xl font-semibold tracking-tight"
          aria-label="AcademIA"
        >
          <img
            src={isotipoImg}
            alt=""
            width={40}
            height={40}
            className="h-9 w-9 shrink-0 object-contain sm:h-10 sm:w-10"
          />
          <span className="flex items-baseline">
            <span className="text-landing-text">Academ</span>
            <span className="text-landing-red">IA</span>
          </span>
        </Link>

        {/* Derecha: nav pegado a las acciones (Cart, Accedé/Registro o, si está logueado,
            su botón + menú).

            Las acciones se muestran TAMBIÉN en mobile. Antes vivían sólo detrás de la
            hamburguesa, y al vaciarse `navLinks` ese menú quedó conteniendo únicamente
            estos dos botones: el CTA de registro desaparecía atrás de un tap para nada.
            El nav sí sigue siendo sólo de desktop. */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3 lg:gap-6">
          <div className="hidden items-center gap-6 lg:flex">
            {navLinks.map((link) => {
              // Special handling for "courses" - show dropdown if menu categories exist
              if (link.key === "courses" && menuCategories && menuCategories.length > 0) {
                return (
                  <DropdownMenu key={link.key}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "flex cursor-pointer items-center gap-1 text-sm font-medium transition-colors select-none hover:text-landing-red focus:outline-none",
                          location.pathname.startsWith("/courses")
                            ? "text-landing-text"
                            : "text-landing-muted",
                        )}
                      >
                        {t(`landing.nav.${link.key}`)}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[200px]">
                      {menuCategories.map((cat) => (
                        <DropdownMenuItem key={cat.id} asChild>
                          <Link
                            to={`/courses/category/${cat.slug}`}
                            className={cn(
                              "flex cursor-pointer items-center px-3 py-2 text-sm",
                              location.pathname === `/courses/category/${cat.slug}`
                                ? "font-medium text-landing-text"
                                : "text-landing-muted",
                            )}
                          >
                            {getCategoryTitle(cat.title)}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link
                          to="/courses"
                          className="flex cursor-pointer items-center px-3 py-2 text-sm font-medium text-landing-text"
                        >
                          {t("landing.nav.viewAllCourses", "View all courses")}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return link.children ? (
                <DropdownMenu key={link.key}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "flex cursor-pointer items-center gap-1 text-sm font-medium transition-colors select-none hover:text-landing-red focus:outline-none",
                        location.pathname.startsWith(link.href)
                          ? "text-landing-text"
                          : "text-landing-muted",
                      )}
                    >
                      {t(`landing.nav.${link.key}`)}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px]">
                    <DropdownMenuItem asChild>
                      <Link
                        to={link.href}
                        className="flex cursor-pointer items-center px-3 py-2 text-sm font-medium text-landing-text"
                      >
                        {t(`landing.nav.${link.key}All`)}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.key} asChild>
                        <Link
                          to={child.href}
                          className={cn(
                            "flex cursor-pointer items-center px-3 py-2 text-sm",
                            location.pathname === child.href
                              ? "font-medium text-landing-text"
                              : "text-landing-muted",
                          )}
                        >
                          {t(`landing.nav.${child.key}`)}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.key}
                  to={link.href}
                  data-cuelume-hover="tick"
                  data-cuelume-press
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-landing-text",
                    location.pathname === link.href ? "text-landing-text" : "text-landing-muted",
                  )}
                >
                  {t(`landing.nav.${link.key}`)}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <CartIcon />
            {authPending ? (
              // Sesión desconocida todavía: un hueco de las mismas medidas que el estado
              // resuelto. Ni "Ingresá" ni el avatar — cualquiera de los dos sería una
              // afirmación que aún no podemos hacer, y una de ellas va a estar mal.
              <div aria-hidden="true" className="flex items-center gap-2 sm:gap-3">
                <span className="h-9 w-[104px] animate-pulse rounded-[8px] bg-landing-border/60 sm:w-[120px]" />
                <span className="h-9 w-9 animate-pulse rounded-full bg-landing-border/60" />
              </div>
            ) : isLoggedIn ? (
              <>
                <Link
                  to={classroomHref}
                  data-cuelume-press
                  data-cuelume-release
                  aria-label={t("landing.userMenu.classroom")}
                  // En mobile es sólo el ícono (el ancho lo necesitan el logo y el
                  // avatar); de sm para arriba, ícono + "Mi Aula".
                  className="inline-flex items-center gap-2 rounded-[8px] bg-landing-red px-2.5 py-2 text-[13px] font-bold text-white shadow-landing-red transition-all hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:px-4 sm:text-sm"
                >
                  <Icon name="Course" className="h-[18px] w-[18px]" />
                  <span className="max-sm:sr-only">{t("landing.userMenu.classroom")}</span>
                </Link>
                <UserDropdown />
              </>
            ) : (
              <>
                {/* Compactos abajo de sm para que logo + las dos acciones entren en 390px */}
                <Button
                  variant="ghost"
                  asChild
                  className="px-2 text-[13px] text-landing-text hover:bg-landing-surface sm:px-4 sm:text-sm"
                >
                  <Link to="/auth/login" data-cuelume-hover="tick" data-cuelume-press>
                    {t("landing.auth.login")}
                  </Link>
                </Button>
                <Button
                  asChild
                  className="bg-landing-red px-3 text-[13px] text-white shadow-landing-red hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:px-4 sm:text-sm"
                >
                  <Link to="/auth/register" data-cuelume-press data-cuelume-release>
                    {t("landing.auth.register")}
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* La hamburguesa sólo existe si hay algo que navegar. Con `navLinks` vacío abría
            un panel con nada adentro salvo los botones de auth, que ahora están en la
            barra. Si se restauran links, vuelve sola. */}
        {navLinks.length > 0 && (
          <button
            type="button"
            // -m-2.5 compensa el padding: el área táctil sube a 44px sin correr el ícono.
            className="-m-2.5 p-2.5 lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-cuelume-press
            aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
          >
            <Icon
              name={mobileMenuOpen ? "HamburgerMenuClose" : "HamburgerMenu"}
              className="h-6 w-6 text-landing-text"
            />
          </button>
        )}
      </nav>

      {navLinks.length > 0 && mobileMenuOpen && (
        <div className="border-t border-landing-border bg-landing-cream lg:hidden">
          <div className="space-y-1 px-4 py-4">
            {navLinks.map((link) => {
              // Special handling for "courses" in mobile
              if (link.key === "courses" && menuCategories && menuCategories.length > 0) {
                return (
                  <div key={link.key}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base font-medium transition-colors",
                        location.pathname.startsWith("/courses")
                          ? "bg-landing-red/10 text-landing-red"
                          : "text-landing-text hover:bg-landing-surface",
                      )}
                      onClick={() => setCoursesExpanded(!coursesExpanded)}
                    >
                      {t(`landing.nav.${link.key}`)}
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform",
                          coursesExpanded && "rotate-180",
                        )}
                      />
                    </button>
                    {coursesExpanded && (
                      <div className="ml-4 space-y-1 mt-1">
                        {menuCategories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/courses/category/${cat.slug}`}
                            className={cn(
                              "block rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                              location.pathname === `/courses/category/${cat.slug}`
                                ? "text-landing-text"
                                : "text-landing-muted hover:bg-landing-surface hover:text-landing-text",
                            )}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {getCategoryTitle(cat.title)}
                          </Link>
                        ))}
                        <Link
                          to="/courses"
                          className="block rounded-lg px-3 py-1.5 text-sm font-medium text-landing-text/80 hover:bg-landing-surface"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t("landing.nav.viewAllCourses", "View all courses")}
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }

              return link.children ? (
                <div key={link.key}>
                  <Link
                    to={link.href}
                    className={cn(
                      "block rounded-lg px-3 py-2.5 text-base font-medium transition-colors",
                      location.pathname.startsWith(link.href)
                        ? "bg-landing-red/10 text-landing-red"
                        : "text-landing-text hover:bg-landing-surface",
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(`landing.nav.${link.key}`)}
                  </Link>
                  <div className="ml-4 space-y-1">
                    {link.children.map((child) => (
                      <Link
                        key={child.key}
                        to={child.href}
                        className={cn(
                          "block rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                          location.pathname === child.href
                            ? "text-landing-text"
                            : "text-landing-muted hover:bg-landing-surface hover:text-landing-text",
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t(`landing.nav.${child.key}`)}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.key}
                  to={link.href}
                  data-cuelume-press
                  className={cn(
                    "block rounded-lg px-3 py-2.5 text-base font-medium transition-colors",
                    location.pathname === link.href
                      ? "bg-landing-red/10 text-landing-red"
                      : "text-landing-text hover:bg-landing-surface",
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(`landing.nav.${link.key}`)}
                </Link>
              );
            })}
            <div className="mt-4 flex flex-col gap-2 border-t border-landing-border pt-4">
              {isLoggedIn ? (
                <MobileUserMenu />
              ) : (
                <>
                  {/* h-11 en el menú mobile: el alto por defecto del Button son 40px y
                      queda por debajo del mínimo táctil de 44px. */}
                  <Button
                    variant="outline"
                    asChild
                    className="h-11 w-full border-landing-border text-landing-text hover:bg-landing-surface"
                  >
                    <Link to="/auth/login" data-cuelume-hover="tick" data-cuelume-press>
                      {t("landing.auth.login")}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className="h-11 w-full bg-landing-red text-white shadow-landing-red hover:bg-landing-red-hover"
                  >
                    <Link to="/auth/register" data-cuelume-press data-cuelume-release>
                      {t("landing.auth.register")}
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
